import React from "react";

import type { PalletSummary } from "@/api/order_check";
import { cn } from "@/lib/utils";
import { palletNumber } from "@/pages/order-check/v2/pallet-number";

/** Пустая паллета при завершении МОЛЧА выпадает из отгрузки: бэкенд
 *  (`order_check/cargo.py::close_pallets`) аннулирует её грузоместо реестра
 *  и этикетку не печатает. 23.10.2026, заказ
 *  НФНФ-000711: товар паллеты 9 (№ 00000001262) после смены партии в 1С
 *  отсканировали на паллету 11 — паллета 9 опустела и пропала, склад узнал
 *  по дыре в нумерации. Поэтому пустую показываем ЗАРАНЕЕ (бейдж в чипах и
 *  на странице паллеты) и громко — в диалоге завершения. Это предупреждение,
 *  не запрет (решение владельца 23.10.2026). */
export function emptyPallets(pallets: PalletSummary[]): PalletSummary[] {
  return pallets.filter((p) => p.items_count === 0);
}

function palletTitle(p: PalletSummary): string {
  const name = p.label ?? `Паллета ${p.position}`;
  const number = palletNumber(p);
  return number ? `${name} (№ ${number})` : name;
}

/** Что станет с пустой паллетой при завершении — ровно по бэкенду: свою
 *  (грузоместо реестра, номер — SSCC) аннулируем, её этикетка недействительна
 *  (`cargo.close_pallets`). Плановую (бота) бэкенд не трогает, а сверка плана
 *  (`plan.pallet_discrepancies`) завершение с пустой плановой паллетой
 *  отклонит — «не войдёт в отгрузку» про неё неправда. */
function fateLine(p: PalletSummary): string {
  if (p.is_planned) return " пустая — это паллета из плана 1С, её нужно собрать по плану.";
  if (p.sscc) return " пустая — она не войдёт в отгрузку, паллета будет аннулирована (этикетку снимите).";
  return " пустая — она не войдёт в отгрузку.";
}

/** Блок-предупреждение для диалога «Завершить проверку?». */
export function EmptyPalletsWarning({ pallets }: { pallets: PalletSummary[] }): React.ReactElement {
  const many = pallets.length > 1;
  return (
    <div role="alert" className="mt-2 space-y-1 rounded-lg border border-amber-400 bg-amber-50 p-2 text-[12px] text-amber-900">
      {pallets.map((p) => (
        <div key={p.id}>
          <b>{palletTitle(p)}</b>
          {fateLine(p)}
        </div>
      ))}
      {/* ⚠️ Не «отсканируйте на эту паллету»: диалог открывается, только когда
          заказ собран целиком, значит товар уже отсканирован на ДРУГУЮ паллету
          (НФНФ-000711 — на 11-ю), и повторный скан удвоил бы количество. */}
      <div className="font-semibold">
        {many ? "Если на какой-то из них лежит товар" : "Если на ней лежит товар"}, он отсканирован на
        другую паллету — нажмите «Отмена», откройте ту паллету, отметьте позиции и нажмите «Перенести».
      </div>
    </div>
  );
}

/** Маленький бейдж «пустая» — в чипах паллет, сводке активной и шапке страницы паллеты. */
export function EmptyPalletBadge({ className }: { className?: string }): React.ReactElement {
  return (
    <span className={cn("rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800", className)}>
      пустая
    </span>
  );
}
